/**
 * Central billing return contract (suite contract §1.2).
 *
 * Every Stripe Checkout started from Central returns to ONE Central page,
 * `/billing/return`, which shows the result and then sends the browser to a
 * VALIDATED `return_to` with `billing=success|cancel` appended:
 *
 *   ${CENTRAL}/billing/return
 *       ?status=success|cancel
 *       &kind=plan|plan_change|credits
 *       &workspaceId=<workspace id>
 *       [&plan=<plan code>]                    (plan checkout / change)
 *       [&return_to=<absolute app URL | Central path>]
 *       [&session_id={CHECKOUT_SESSION_ID}]    (Stripe fills this in)
 *
 * `return_to` is never trusted from the URL: a Central path must pass
 * `safeContinuePath`; an absolute URL must pass Central's redirect allowlist
 * (GET /api/billing/return-target). Anything else falls back to the
 * workspace's own billing / credits settings page (or /home).
 */

import { safeContinuePath } from './safeContinuePath';

export type BillingResult = 'success' | 'cancel';
export type BillingKind = 'plan' | 'plan_change' | 'credits';

/** Stripe replaces this literal token; it must NOT be percent-encoded. */
export const STRIPE_SESSION_TOKEN = '{CHECKOUT_SESSION_ID}';

const WORKSPACE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function isPlausibleWorkspaceId(value: unknown): value is string {
  return typeof value === 'string' && WORKSPACE_ID_RE.test(value);
}

export function buildBillingReturnUrl(input: {
  origin: string;
  status: BillingResult;
  kind: BillingKind;
  workspaceId: string;
  returnTo?: string | null;
  plan?: string | null;
  withStripeSessionId?: boolean;
}): string {
  const params = new URLSearchParams();
  params.set('status', input.status);
  params.set('kind', input.kind);
  params.set('workspaceId', input.workspaceId);
  if (input.plan) params.set('plan', input.plan);
  if (input.returnTo) params.set('return_to', input.returnTo);
  let url = `${input.origin}/billing/return?${params.toString()}`;
  if (input.withStripeSessionId) url += `&session_id=${STRIPE_SESSION_TOKEN}`;
  return url;
}

const ABSOLUTE_HTTP = /^https?:\/\//i;
const BACKSLASH = String.fromCharCode(92);

/**
 * A Central path → an ABSOLUTE same-origin href, or null.
 *
 * CEN-BILL-2: never hand a bare pathname to a navigation sink. A path that
 * normalises to '//host' (e.g. '/.//evil.example') is scheme-relative when
 * used on its own; qualified with this origin it cannot leave the origin. The
 * path must also pass safeContinuePath, and the resolved URL is re-checked.
 */
export function toSameOriginHref(path: string): string | null {
  if (safeContinuePath(path) === null) return null;
  let url: URL;
  try {
    url = new URL(path, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (url.pathname.startsWith('//') || url.pathname.includes(BACKSLASH)) return null;
  return url.href;
}

/**
 * Append `billing=<result>` to an ABSOLUTE http(s) href (as produced by
 * resolveReturnTo / toSameOriginHref). Returns the normalised href, or null
 * for anything that is not an absolute http(s) URL — callers fall back.
 */
export function appendBillingResult(target: string, result: BillingResult): string | null {
  if (!ABSOLUTE_HTTP.test(target)) return null;
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  url.searchParams.set('billing', result);
  return url.href;
}

/** Default destination (a Central path) when no valid return_to was supplied. */
export function billingFallbackPath(workspaceId: string | null, kind: BillingKind | null): string {
  if (!isPlausibleWorkspaceId(workspaceId)) return '/home';
  return `/workspace/${workspaceId}/settings/${kind === 'credits' ? 'credits' : 'billing'}`;
}

/**
 * Resolve a caller-supplied return_to (or returnUrl) to a SAFE ABSOLUTE href,
 * or null. Used by every Central navigation that follows a return_to
 * (/billing/return, the settings Back button).
 *
 *   - a Central path must pass safeContinuePath and stay on this origin
 *     (returned origin-qualified, never as a bare path);
 *   - an absolute URL must literally start with http:// or https:// (so
 *     'javascript:', ' https://…', 'https:\\…' and '//host' are refused
 *     without a request) AND be approved by Central's redirect allowlist
 *     (GET /api/billing/return-target); the parsed, normalised href is
 *     returned, never the raw string;
 *   - anything else, or any failed check, is a rejection (null).
 */
export async function resolveReturnTo(
  returnTo: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  if (!returnTo) return null;
  if (returnTo.startsWith('/')) return toSameOriginHref(returnTo);
  if (!ABSOLUTE_HTTP.test(returnTo)) return null;
  let parsed: URL;
  try {
    parsed = new URL(returnTo);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  try {
    const res = await fetchImpl(`/api/billing/return-target?return_to=${encodeURIComponent(returnTo)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data?.valid === true && data.returnTo === returnTo ? parsed.href : null;
  } catch {
    return null;
  }
}
